/**
 * MobileReadinessActionsList — mobile priority-actions list (Batch 7A).
 *
 * Server-authoritative: action_key / area / action / priority / module are
 * rendered verbatim from readiness.recommendations. Persisted owner / due /
 * status come from readinessActions[action_key]. Mutations go through the
 * SAME upsert handler the desktop uses (POST /readiness-actions/upsert) —
 * no new endpoint, no client-side derivation, no deep-link creation.
 *
 * Care Worker (!canManage): the owner Select, due-date input and status
 * Select are NOT rendered — persisted values render as read-only text.
 */
import React from "react";
import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import PersonAvatar from "@/components/PersonAvatar";

const STATUS_LABEL = {
  not_started:       "Not Started",
  in_progress:       "In Progress",
  awaiting_evidence: "Awaiting Evidence",
  completed:         "Completed",
  overdue:           "Overdue",
};

const STATUS_TONE = {
  not_started:       "bg-slate-100 text-slate-700 border-slate-300",
  in_progress:       "bg-sky-100 text-sky-800 border-sky-300",
  awaiting_evidence: "bg-amber-100 text-amber-900 border-amber-300",
  completed:         "bg-emerald-100 text-emerald-800 border-emerald-300",
  overdue:           "bg-red-100 text-red-800 border-red-300",
};

function ActionCard({ r, i, persisted, staffList, canManage, onUpsert, onOpenHistory }) {
  const status = persisted?.status || "not_started";
  const statusTone = STATUS_TONE[status];

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-3" data-testid={`mobile-readiness-action-${i}`}>
      <div className="flex items-start justify-between gap-2">
        <span
          className={`text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded border shrink-0 ${
            r.priority === "high"
              ? "border-red-300 bg-red-50 text-red-800"
              : "border-amber-300 bg-amber-50 text-amber-800"
          }`}
          data-testid={`mobile-readiness-action-priority-${i}`}
        >
          {r.priority}
        </span>
        <span
          className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[10px] font-semibold shrink-0 ${statusTone}`}
          data-testid={`mobile-readiness-action-status-badge-${i}`}
        >
          {STATUS_LABEL[status] || status}
        </span>
      </div>

      <Link
        to={`/${r.module}`}
        className="block mt-2 text-[13px] text-slate-900 hover:text-sky-700 break-words leading-snug min-h-[44px]"
        data-testid={`mobile-readiness-action-text-${i}`}
      >
        {r.action}
      </Link>
      <div className="text-[10px] text-slate-500 mt-0.5">{r.area}</div>

      {/* Owner + Due row */}
      <div className="mt-3 grid grid-cols-1 gap-2 text-[12px]">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Owner</div>
          {canManage ? (
            <div className="flex items-center gap-2">
              <select
                value={persisted?.owner_id || ""}
                onChange={(e) => {
                  const st = staffList.find((s) => s.id === e.target.value);
                  onUpsert(r, persisted, { owner_id: st?.id || null, owner_name: st?.name || null });
                }}
                className="text-[12px] h-11 flex-1 rounded border border-slate-300 bg-white px-2 min-w-0"
                data-testid={`mobile-action-owner-${i}`}
              >
                <option value="">Unassigned</option>
                {staffList.map((s) => (
                  <option key={s.id} value={s.id}>{s.name || s.email}</option>
                ))}
              </select>
              {persisted?.owner_id && (
                <div className="shrink-0" data-testid={`mobile-action-owner-avatar-${i}`}>
                  <PersonAvatar person={staffList.find((s) => s.id === persisted.owner_id) || { name: persisted.owner_name }} size="sm" ring />
                </div>
              )}
            </div>
          ) : (
            <div className="text-[12px] text-slate-700" data-testid={`mobile-action-owner-readonly-${i}`}>
              {persisted?.owner_name || "Unassigned"}
            </div>
          )}
        </div>

        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Due</div>
          {canManage ? (
            <input
              type="date"
              value={persisted?.due_date || ""}
              onChange={(e) => onUpsert(r, persisted, { due_date: e.target.value || null })}
              className="text-[12px] h-11 w-full rounded border border-slate-300 bg-white px-2"
              data-testid={`mobile-action-due-${i}`}
            />
          ) : (
            <div className="text-[12px] text-slate-700" data-testid={`mobile-action-due-readonly-${i}`}>
              {persisted?.due_date || "No date"}
            </div>
          )}
        </div>

        {canManage && (
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Status</div>
            <select
              value={status}
              onChange={(e) => onUpsert(r, persisted, { status: e.target.value })}
              className={`text-[12px] h-11 w-full rounded border px-2 font-semibold ${statusTone}`}
              data-testid={`mobile-action-status-${i}`}
            >
              <option value="not_started">Not Started</option>
              <option value="in_progress">In Progress</option>
              <option value="awaiting_evidence">Awaiting Evidence</option>
              <option value="completed">Completed</option>
              <option value="overdue">Overdue</option>
            </select>
          </div>
        )}
      </div>

      <div className="mt-3 flex items-center justify-between">
        <button
          type="button"
          onClick={() => onOpenHistory(r)}
          className="text-[11px] uppercase tracking-widest text-slate-500 hover:text-sky-700 min-h-[44px] inline-flex items-center gap-1"
          data-testid={`mobile-action-history-${i}`}
        >
          View log <ChevronRight className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
}

export default function MobileReadinessActionsList({
  recommendations, readinessActions, staffList, canManage, onUpsert, onOpenHistory,
}) {
  const recs = Array.isArray(recommendations) ? recommendations : [];
  if (recs.length === 0) {
    return (
      <div className="bg-white border border-slate-200 rounded-lg p-4 text-[13px] text-slate-500" data-testid="mobile-readiness-no-actions">
        No outstanding priority actions.
      </div>
    );
  }
  return (
    <div className="space-y-2" data-testid="mobile-readiness-actions-list">
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold px-1">
        Priority Actions Before Inspection
      </div>
      {recs.map((r, i) => (
        <ActionCard
          key={r.action_key}
          r={r}
          i={i}
          persisted={readinessActions?.[r.action_key] || {}}
          staffList={staffList || []}
          canManage={canManage}
          onUpsert={onUpsert}
          onOpenHistory={onOpenHistory}
        />
      ))}
    </div>
  );
}
